/**
 * NHTSA vPIC VIN-decoder helper.
 *
 * The /estimates/new form uses this to fill year/make/model/engine
 * inline as the mechanic types a VIN. Public, no-auth API — see
 * https://vpic.nhtsa.dot.gov/api/. Free to use; no rate-limit headers
 * documented but informal community guidance is "be polite", which is
 * easy here since we only call once per VIN entry.
 *
 * VINs are 17 characters, alphanumeric, no I/O/Q (those letters look
 * like 1/0). We validate cheaply before issuing a request — the API
 * will return decoded results for shorter strings ("partial VIN"
 * mode), but for our use case we only want to fire on a full VIN to
 * keep the UX from flickering as the user types.
 */

const NHTSA_VIN_BASE =
  'https://vpic.nhtsa.dot.gov/api/vehicles/decodevin'

const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/i

export type DecodedVin = {
  vin: string
  year: number | null
  make: string | null
  model: string | null
  engine: string | null
}

export function isValidVin(vin: string): boolean {
  return VIN_REGEX.test(vin.trim())
}

type NhtsaResult = {
  Variable: string | null
  VariableId: number | null
  Value: string | null
  ValueId: string | null
}

type NhtsaResponse = {
  Count?: number
  Message?: string
  Results?: NhtsaResult[]
}

/**
 * Pulls fields by VariableId — these are stable across NHTSA's API
 * versions, while Variable name strings have rotated in the past
 * ("Engine Configuration" → "Engine Number of Cylinders" → etc.).
 */
const FIELD_IDS = {
  year: 29, // Model Year
  make: 26, // Make
  model: 28, // Model
  cylinders: 9, // Engine Number of Cylinders
  displacementL: 13, // Displacement (L)
  fuelType: 24, // Fuel Type - Primary
} as const

function pickById(results: NhtsaResult[], id: number): string | null {
  const row = results.find((r) => r.VariableId === id)
  const v = row?.Value ?? null
  if (!v) return null
  const trimmed = v.trim()
  return trimmed.length > 0 && trimmed !== 'Not Applicable' ? trimmed : null
}

function buildEngineLabel(results: NhtsaResult[]): string | null {
  const cyl = pickById(results, FIELD_IDS.cylinders)
  const disp = pickById(results, FIELD_IDS.displacementL)
  const fuel = pickById(results, FIELD_IDS.fuelType)
  // Format as "2.0L 4cyl Gasoline" when all three are present, gracefully
  // truncate when partial. Keeps the engine cell readable on mobile
  // without truncation truncation chains in the form UI.
  const dispLabel = disp ? `${disp}L` : null
  const cylLabel = cyl ? `${cyl}cyl` : null
  const parts = [dispLabel, cylLabel, fuel].filter(Boolean) as string[]
  return parts.length > 0 ? parts.join(' ') : null
}

export async function decodeVin(
  vin: string,
  init: { signal?: AbortSignal } = {},
): Promise<DecodedVin> {
  const normalized = vin.trim().toUpperCase()
  if (!isValidVin(normalized)) {
    throw new Error('invalid_vin')
  }

  const url = `${NHTSA_VIN_BASE}/${encodeURIComponent(normalized)}?format=json`
  const res = await fetch(url, { signal: init.signal })
  if (!res.ok) {
    throw new Error(`nhtsa_request_failed_${res.status}`)
  }
  const json = (await res.json()) as NhtsaResponse
  const results = json.Results ?? []

  const yearStr = pickById(results, FIELD_IDS.year)
  const year = yearStr ? Number.parseInt(yearStr, 10) : null

  return {
    vin: normalized,
    year: Number.isFinite(year) ? (year as number) : null,
    make: pickById(results, FIELD_IDS.make),
    model: pickById(results, FIELD_IDS.model),
    engine: buildEngineLabel(results),
  }
}
